import { Box, Stack } from '@mui/material';
import { useCallback } from 'react';
import { SetOptional } from 'type-fest';

import {
  FormModal,
  FormModalProps,
} from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions/FormModalActions';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useFormContext } from '@/components/react-hook-form';
import { useForm, useSubmitSuccessHandler } from '@/components/react-hook-form';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';

import { Operations as AdminTeamsOperations } from '../../../pages/admin/AdminTeamsPage/graphql/AdminTeams.generated';
import { Operations as TeamsTypeaheadOperations } from '../components/TeamsTypeahead/graphql/TeamsTypeahead.generated';
import {
  mapFormDataToUpdateInput,
  useSyncTeamDataToForm,
} from './EditTeamModal.utils';
import { useDeleteTeamMutation } from './graphql/DeleteTeam.generated';
import { useUpdateTeamMutation } from './graphql/TeamModal.generated';
import { TeamForm } from './TeamForm';
import { getDefaultValues } from './TeamModal.constants';
import { TeamFormShape } from './TeamModal.types';

type FormModalOptionalProps = SetOptional<FormModalProps, 'heading'> & {
  teamId: string;
};
export type EditTeamModalProps = FormModalOptionalProps;

function EditTeam({
  onClose,
  heading = 'Edit team',
  teamId,
  ...formModalProps
}: FormModalOptionalProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { formRef, handleSave } = useFormSaveHandler();
  const trackUserEvent = useTrackUserEvent();
  const { handleSubmit, reset, shouldBlockNavigation, formState } =
    useFormContext<TeamFormShape>();
  const { defaultValues } = formState;

  const [updateMutation, { loading: isSubmitting }] = useUpdateTeamMutation({
    onError: (error) => {
      showFeedback(FeedbackMessages.formSaveError);
      reportError(`Could not edit team`, error);
    },
    onCompleted: () => {
      showFeedback('Successfully updated team', { variant: 'success' });
    },
  });

  const [deleteTeam, { loading: isDeleting }] = useDeleteTeamMutation({
    refetchQueries: [
      AdminTeamsOperations.Query.GetAdminTeams,
      TeamsTypeaheadOperations.Query.GetTeamsMultiple,
    ],
    onError: (error) => {
      showFeedback(FeedbackMessages.formSaveError);
      reportError('Could not delete team', error);
    },
    onCompleted: () => {
      showFeedback('Team deleted successfully.', {
        variant: 'success',
      });
      onClose();
    },
  });

  const onSubmit = handleSubmit((formData) => {
    trackUserEvent('team updated', {
      teamId,
      assignedHouseholdCount: formData.assignedHouseholdIds.length,
    });

    const input = mapFormDataToUpdateInput(
      defaultValues as TeamFormShape,
      formData,
      teamId
    );

    return updateMutation({ variables: { input } });
  });

  const handleDeleteTeam = useCallback(async () => {
    void deleteTeam({
      variables: {
        teamId,
      },
    });
  }, [deleteTeam, teamId]);

  const closeModal = () => {
    reset();
    onClose();
  };

  const { team } = useSyncTeamDataToForm(reset, teamId, onClose);
  const scimManaged = Boolean(team?.workosDirectoryGroupID);

  useSubmitSuccessHandler(() => {
    closeModal();
  });

  return (
    <FormModal
      {...formModalProps}
      onClose={onClose}
      heading={heading}
      shouldBlockClose={shouldBlockNavigation}
      actions={
        <FormModalActions.Provider formState={formState}>
          {!scimManaged && (
            <Stack spacing={0.5} direction="row">
              <FormModalActions.DeleteButton
                onConfirmDelete={handleDeleteTeam}
                loading={isDeleting}
                disabled={isSubmitting}
                promptDeleteText="Delete team"
                confirmDeleteText="Confirm delete"
              />
            </Stack>
          )}
          <Box flexGrow={1} />
          <FormModalActions.CancelButton onClick={closeModal} />
          <FormModalActions.SaveButton
            disabled={isDeleting}
            onClick={handleSave}
          >
            Save changes
          </FormModalActions.SaveButton>
        </FormModalActions.Provider>
      }
    >
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <TeamForm scimManaged={scimManaged} />
      </form>
    </FormModal>
  );
}

export const EditTeamModal = ({ teamId, ...props }: EditTeamModalProps) => {
  const formMethods = useForm<TeamFormShape>({
    defaultValues: getDefaultValues(),
  });

  return (
    <FormProvider {...formMethods}>
      <EditTeam {...props} teamId={teamId} />
    </FormProvider>
  );
};
